import React, {FC, memo} from "react";
import {ColumnProps, TablePaginationConfig, TableProps} from "antd/lib/table";
import {Table} from "antd";
import {Moment} from "moment";
import classNames from "classnames";
import css from "./LessonBookings.less";
import {
    ArrayDataOptions,
    CourseLessonBooking,
    GetBookingByIdQuery,
    GetBookingByIdWithCourseLessonBookingsQuery,
    GetCourseLessonsByCourseIdMinimalQuery,
    GetHolidaysByCompanyAndDateRangeQuery,
    Maybe,
    useGetCourseLessonsByCourseIdMinimalQuery,
} from "../../../generated/graphql";
import i18n from "../../../services/i18n";
import {renderDateAsWeekdayTag, renderDateFormatted} from "../../../views/TableColumnRenderers/TableColumnRenderers";
import {renderIndex} from "./helper";
import {defaultPageSize} from "./ModalContainer";
import {getPagination} from "../../../helpers/getPagination";

export type LessonBookingCourseLessonTableData = GetCourseLessonsByCourseIdMinimalQuery["courseLessonsByCourseId"][number];
type LessonTablePassiveProps = {
    booking: (GetBookingByIdQuery["bookingById"] & {courseLessonBookings?: Array<CourseLessonBooking>}) | GetBookingByIdWithCourseLessonBookingsQuery["bookingByIdWithCourseLessonBookings"];
    getHolidayInfo: (date: Moment) => GetHolidaysByCompanyAndDateRangeQuery["holidaysByCompanyAndDateRange"][number] | undefined;
    pagination?: TableProps<LessonBookingCourseLessonTableData>["pagination"];
    options?: Maybe<ArrayDataOptions>;
    setOptions: (options: ArrayDataOptions) => void;
    extraSmall?: boolean;
};

const LessonTablePassive: FC<LessonTablePassiveProps> = ({booking, getHolidayInfo, pagination, options, setOptions, extraSmall = false}) => {
    const {data, loading} = useGetCourseLessonsByCourseIdMinimalQuery({
        skip: !booking.courseId,
        variables: {
            courseId: booking.courseId ?? "",
        },
    });
    const lessonsOfTheCourse = data?.courseLessonsByCourseId;

    const columns: Array<ColumnProps<LessonBookingCourseLessonTableData>> = [
        {
            title: "#",
            dataIndex: "number",
            align: "center",
            render: (text, record, rowIndex) => renderIndex(record?.indexOfLessonInCourse ?? rowIndex, getHolidayInfo(record.startDateTime)),
        },
        {
            title: i18n.containers.courses.CourseCreate.courseStartDateTime.weekday(),
            dataIndex: "dayOfTheWeek",
            render: (text, record) =>
                renderDateAsWeekdayTag({
                    value: record.startDateTime,
                    style: {
                        width: "100%",
                        textAlign: "center",
                        fontWeight: 600,
                    },
                }),
        },
        {
            title: i18n.containers.courses.CourseCreate.courseStartDateTime.date(),
            dataIndex: "startDateTime",
            render: (text, record) => renderDateFormatted(record.startDateTime, "DD. MMM YYYY"),
        },
    ];

    const onChange: (pagination: TablePaginationConfig) => void = (pagination) => {
        const updatedOptions = {
            ...options,
            limit: getPagination({
                pagination,
                defaultSize: defaultPageSize,
            }).limit,
            offset: getPagination({
                pagination,
                defaultSize: defaultPageSize,
            }).offset,
        };

        setOptions(updatedOptions);
    };

    const selectedLessonIds = booking.courseLessonBookings?.map((courseLessonBooking) => {
        return courseLessonBooking.courseLessonId;
    });

    const renderTable = () => {
        return (
            <Table<LessonBookingCourseLessonTableData>
                className="lessonSelectionTable"
                showHeader={!extraSmall}
                loading={loading}
                rowKey="id"
                size="small"
                columns={columns}
                dataSource={lessonsOfTheCourse}
                rowSelection={{
                    type: "checkbox",
                    hideSelectAll: true,
                    selectedRowKeys: selectedLessonIds,
                    getCheckboxProps: (record) => {
                        return {
                            disabled: true,
                        };
                    },
                }}
                // rowClassName={css.extraSmallRow}
                rowClassName={(record) => {
                    if (selectedLessonIds?.includes(record.id)) {
                        return classNames(css.extraSmallRow, css.bookedLesson);
                    }

                    return classNames(css.extraSmallRow, css.unbookedLesson);
                }}
                pagination={
                    pagination ?? {
                        current: options?.offset && options?.limit ? options.offset / options.limit + 1 : 1,
                        pageSize: options?.limit ?? defaultPageSize,
                        showSizeChanger: true,
                        pageSizeOptions: ["10", "15", "20", "25", "50", "100"],
                        defaultPageSize,
                        total: lessonsOfTheCourse?.length ?? 0,
                        size: "small",
                        showTotal: (total, range) => `${range[0]}-${range[1]} von ${total}`,
                    }
                }
                onChange={onChange}
            />
        );
    };

    return (
        <>
            {!extraSmall && <div style={{height: "38px"}} />} {/* Spacer */}
            {renderTable()}
        </>
    );
};

export default memo(LessonTablePassive);
